import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { apiMutate } from '../composables/apiMutate'

// Admin-only store for short-lived public share links.
//
// Mirrors the api_tokens "shown once" contract: POST returns the full
// `/s/<token>` URL, but only the digest is stored server-side, so the
// list (GET) returns metadata only and the URL can never be re-fetched.
// `lastCreatedUrl` holds the URL in memory just long enough for the
// create modal to surface + copy it, then is dropped.

export type ShareTtl = '1h' | '24h' | '7d'

export interface ShareRow {
  id: number
  page_path: string | null
  page_title: string | null
  expires_at: string | null
  revoked_at: string | null
  created_at: string
}

interface CreatedShare extends ShareRow {
  url: string
}

export const useSharesStore = defineStore('shares', () => {
  const lastCreatedUrl = ref<string | null>(null)

  const {
    data: shares,
    loaded,
    error: loadError,
    refresh: fetchAll,
  } = useAsyncResource<ShareRow[]>(
    async () => {
      const res = await fetch('/api/v1/shares', { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load shares: ${res.status}`)
      return res.json()
    },
    { initialValue: [] },
  )

  async function create(wikiPageId: number, ttl: ShareTtl): Promise<CreatedShare> {
    const created = await apiMutate<CreatedShare>(`/api/v1/wiki_pages/${wikiPageId}/shares`, {
      method: 'POST',
      body: { ttl },
      failureLabel: 'failed to create share link',
    })
    const { url, ...metadata } = created
    shares.value = [...shares.value, metadata]
    // The API returns a root-relative `/s/<token>` path; resolve it
    // against the origin the admin is actually on so the copied link is
    // reachable (the SPA's :5173 in dev, the real domain in prod) — see
    // PageShareSerializer.
    lastCreatedUrl.value = new URL(url, window.location.origin).toString()
    return created
  }

  async function revoke(id: number) {
    await apiMutate(`/api/v1/shares/${id}`, {
      method: 'DELETE',
      failureLabel: 'failed to revoke share',
    })
    shares.value = shares.value.filter((s) => s.id !== id)
  }

  function forgetLastCreatedUrl() {
    lastCreatedUrl.value = null
  }

  return {
    shares,
    loaded,
    loadError,
    lastCreatedUrl,
    fetchAll,
    create,
    revoke,
    forgetLastCreatedUrl,
  }
})
